import { createApi } from "@reduxjs/toolkit/query/react";
import { apiFunction } from "./utils";
import { baseQueryWithReauth } from "./baseQueryWithReauth";

export const doctorApi = createApi({
  reducerPath: "doctorApi",
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,
  tagTypes: ["DoctorPatients", "DoctorAppointments", "DoctorTemplates"],
  endpoints: (builder) => ({
    // Patients
    listDoctorPatients: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/users/doctor/patients/list", "POST"),
      invalidatesTags: ["DoctorPatients"],
    }),

    // Appointments
    listDoctorAppointments: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/appointments/doctor-list", "POST"),
      invalidatesTags: ["DoctorAppointments"],
    }),

    // Doctor Medicine Templates
    listDoctorMedicineTemplates: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/medicines/list", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    createDoctorMedicineTemplate: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/medicines/create", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    updateDoctorMedicineTemplate: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/medicines/update", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    setDoctorMedicineTemplateStatus: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/medicines/status", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),

    // Doctor Diet Templates
    listDoctorDietTemplates: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/diets/list", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    createDoctorDietTemplate: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/diets/create", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    updateDoctorDietTemplate: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/diets/update", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    setDoctorDietTemplateStatus: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/diets/status", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),

    // Doctor Görüntüleme (Imaging) Templates
    listDoctorImagingTemplates: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/imaging/list", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    createDoctorImagingTemplate: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/imaging/create", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    updateDoctorImagingTemplate: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/imaging/update", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    setDoctorImagingTemplateStatus: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/imaging/status", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    // Doctor Labratuvar (Lab Test) Templates
    listDoctorLabTestTemplates: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/labs/list", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    createDoctorLabTestTemplate: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/labs/create", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    updateDoctorLabTestTemplate: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/labs/update", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    setDoctorLabTestTemplateStatus: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/labs/status", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),

    // Doctor Diagnosis Templates
    listDoctorDiagnosisTemplates: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/diagnoses/list", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    createDoctorDiagnosisTemplate: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/diagnoses/create", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    updateDoctorDiagnosisTemplate: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/diagnoses/update", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    setDoctorDiagnosisTemplateStatus: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/diagnoses/status", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),

    // Doctor Complaint Templates
    listDoctorComplaintTemplates: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/complaints/list", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    createDoctorComplaintTemplate: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/complaints/create", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    updateDoctorComplaintTemplate: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/complaints/update", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
    setDoctorComplaintTemplateStatus: builder.mutation({
      query: (data) => apiFunction(data, "/api/clinic/doctor-templates/complaints/status", "POST"),
      invalidatesTags: ["DoctorTemplates"],
    }),
  }),
});

export const {
  useListDoctorPatientsMutation,
  useListDoctorAppointmentsMutation,
  useListDoctorMedicineTemplatesMutation,
  useCreateDoctorMedicineTemplateMutation,
  useUpdateDoctorMedicineTemplateMutation,
  useSetDoctorMedicineTemplateStatusMutation,
  useListDoctorDietTemplatesMutation,
  useCreateDoctorDietTemplateMutation,
  useUpdateDoctorDietTemplateMutation,
  useSetDoctorDietTemplateStatusMutation,
  useListDoctorImagingTemplatesMutation,
  useCreateDoctorImagingTemplateMutation,
  useUpdateDoctorImagingTemplateMutation,
  useSetDoctorImagingTemplateStatusMutation,
  useListDoctorLabTestTemplatesMutation,
  useCreateDoctorLabTestTemplateMutation,
  useUpdateDoctorLabTestTemplateMutation,
  useSetDoctorLabTestTemplateStatusMutation,
  useListDoctorDiagnosisTemplatesMutation,
  useCreateDoctorDiagnosisTemplateMutation,
  useUpdateDoctorDiagnosisTemplateMutation,
  useSetDoctorDiagnosisTemplateStatusMutation,
  useListDoctorComplaintTemplatesMutation,
  useCreateDoctorComplaintTemplateMutation,
  useUpdateDoctorComplaintTemplateMutation,
  useSetDoctorComplaintTemplateStatusMutation,
} = doctorApi;
